import * as Dialog from '@radix-ui/react-dialog';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import {
  Archive,
  ArrowDownToLine,
  ArrowLeft,
  ArrowLeftRight,
  ArrowUpFromLine,
  Bot,
  ChevronDown,
  ClipboardList,
  GitCompareArrows,
  History,
  Hourglass,
  LayoutDashboard,
  LogOut,
  Menu,
  Search,
  Settings,
  ShieldCheck,
  Users,
  UserX,
  X,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/auth-context';
import { ParticipantDirectoryProvider } from '@/components/domain/participant-chip';
import { WebScopeIndicator, WebScopeSelect } from '@/components/domain/web-scope-select';
import { Button } from '@/components/ui/button';
import { Pill } from '@/components/ui/primitives';
import { cn, initialsOf } from '@/lib/utils';
import { PaymentFlowLockup, WebsiteLockup } from './brand';
import { LanguageSwitcher } from './language-switcher';
import { ThemeToggle } from './theme';

/**
 * The two application shells.
 *
 * One React app, two areas, two chromes (D-I7). The operator console gets a persistent dark
 * sidebar — it is a workspace people live in for a shift, and a fixed navigation rail is worth
 * the horizontal space. The customer cashier gets a light top bar with three tabs — it is a task
 * someone completes in ninety seconds and leaves.
 *
 * Both collapse to a single column at 375px with the navigation behind a labelled menu button.
 */

interface NavItem {
  to: string;
  labelKey: string;
  icon: typeof LayoutDashboard;
  descriptionKey: string;
  /** Shown only to ADMIN — the target page 403s everyone else, so the link would be a dead end. */
  adminOnly?: boolean;
  /** Shown only to SUPER — the target page 403s everyone else, so the link would be a dead end. */
  superOnly?: boolean;
  /** Rarely used: hidden behind the "more" toggle at the bottom of the rail. */
  secondary?: boolean;
}

const OPERATOR_NAV: NavItem[] = [
  {
    to: '/ops/dashboard',
    labelKey: 'layout.shell.operatorNav.dashboard.label',
    icon: LayoutDashboard,
    descriptionKey: 'layout.shell.operatorNav.dashboard.description',
  },
  {
    // Right after the dashboard: an operator handed a customer complaint rarely knows which
    // screen the money is on, and this is the one box that answers that before they have to
    // guess between the other three.
    to: '/ops/lookup',
    labelKey: 'layout.shell.operatorNav.lookup.label',
    icon: Search,
    descriptionKey: 'layout.shell.operatorNav.lookup.description',
  },
  {
    to: '/ops/reviews',
    labelKey: 'layout.shell.operatorNav.reviews.label',
    icon: ClipboardList,
    descriptionKey: 'layout.shell.operatorNav.reviews.description',
  },
  {
    to: '/ops/withdrawals',
    labelKey: 'layout.shell.operatorNav.withdrawals.label',
    icon: Hourglass,
    descriptionKey: 'layout.shell.operatorNav.withdrawals.description',
  },
  {
    to: '/ops/history',
    labelKey: 'layout.shell.operatorNav.history.label',
    icon: Archive,
    descriptionKey: 'layout.shell.operatorNav.history.description',
  },
  {
    to: '/ops/admin',
    labelKey: 'layout.shell.operatorNav.admin.label',
    icon: ShieldCheck,
    descriptionKey: 'layout.shell.operatorNav.admin.description',
  },
  {
    to: '/ops/operators',
    labelKey: 'layout.shell.operatorNav.operators.label',
    icon: Users,
    descriptionKey: 'layout.shell.operatorNav.operators.description',
    adminOnly: true,
  },
  {
    to: '/ops/clearing',
    labelKey: 'layout.shell.operatorNav.clearing.label',
    icon: ArrowLeftRight,
    descriptionKey: 'layout.shell.operatorNav.clearing.description',
  },
  {
    // Down here with the owner-only tools rather than beside Dashboard: it is a report about how
    // the review queue is being worked, not a queue anyone works, and only SUPER ever sees it.
    to: '/ops/ai-review',
    labelKey: 'layout.shell.operatorNav.aiReview.label',
    icon: Bot,
    descriptionKey: 'layout.shell.operatorNav.aiReview.description',
    superOnly: true,
    secondary: true,
  },
  {
    // A behaviour report, not a queue anyone works — it lives with the owner-side tools.
    to: '/ops/abandonment',
    labelKey: 'layout.shell.operatorNav.abandonment.label',
    icon: UserX,
    descriptionKey: 'layout.shell.operatorNav.abandonment.description',
    secondary: true,
  },
  {
    // Same shelf as abandonment: a read-only observability report, not a queue anyone works —
    // the dashboard's recon-health card and stuck-count tile are where this gets noticed first.
    to: '/ops/ledger-divergences',
    labelKey: 'layout.shell.operatorNav.ledgerDivergences.label',
    icon: GitCompareArrows,
    descriptionKey: 'layout.shell.operatorNav.ledgerDivergences.description',
    secondary: true,
  },
  {
    to: '/ops/settings',
    labelKey: 'layout.shell.operatorNav.settings.label',
    icon: Settings,
    descriptionKey: 'layout.shell.operatorNav.settings.description',
  },
];

const CUSTOMER_NAV: NavItem[] = [
  {
    to: '/app/deposit',
    labelKey: 'layout.shell.customerNav.deposit.label',
    icon: ArrowDownToLine,
    descriptionKey: 'layout.shell.customerNav.deposit.description',
  },
  {
    to: '/app/withdraw',
    labelKey: 'layout.shell.customerNav.withdraw.label',
    icon: ArrowUpFromLine,
    descriptionKey: 'layout.shell.customerNav.withdraw.description',
  },
  {
    to: '/app/history',
    labelKey: 'layout.shell.customerNav.history.label',
    icon: History,
    descriptionKey: 'layout.shell.customerNav.history.description',
  },
];

/**
 * Embedded mode: the cashier lives under the participating website's own chrome (e.g. served at
 * siamplay.com/wallet). The website owns the session, so signing out here would be wrong — the
 * customer instead gets a way back to the page they came from. `VITE_WEBSITE_HOME` overrides the
 * return target when the site's landing page is not the origin root.
 */
const EMBEDDED_CASHIER = import.meta.env.VITE_EMBEDDED_CASHIER === 'true';

function BackToSiteButton(): ReactNode {
  const { t } = useTranslation();
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={() => {
        window.location.href = import.meta.env.VITE_WEBSITE_HOME ?? '/';
      }}
    >
      <ArrowLeft aria-hidden="true" />
      {t('layout.shell.backToSite')}
    </Button>
  );
}

function useIsActive(): (to: string) => boolean {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  return (to: string) => pathname === to || pathname.startsWith(`${to}/`);
}

function SignOutButton({
  variant = 'ghost',
  className,
}: {
  variant?: 'ghost' | 'outline' | undefined;
  className?: string | undefined;
}): ReactNode {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  return (
    <Button
      variant={variant}
      size="sm"
      className={className}
      loading={busy}
      onClick={() => {
        setBusy(true);
        void signOut().finally(() => {
          setBusy(false);
          void navigate({ to: '/login' });
        });
      }}
    >
      <LogOut aria-hidden="true" />
      {t('layout.shell.signOut')}
    </Button>
  );
}

// ---------------------------------------------------------------------------------------------
// Operator shell
// ---------------------------------------------------------------------------------------------

function OperatorNavLink({
  item: { to, labelKey, icon: Icon, descriptionKey },
  onNavigate,
}: {
  item: NavItem;
  onNavigate?: (() => void) | undefined;
}): ReactNode {
  const isActive = useIsActive();
  const { t } = useTranslation();
  const active = isActive(to);
  return (
    <Link
      to={to}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'group flex items-start gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-accent',
        active
          ? 'bg-white/10 text-sidebar-foreground'
          : 'text-sidebar-muted hover:bg-white/5 hover:text-sidebar-foreground',
      )}
    >
      <Icon
        className={cn('mt-0.5 size-4 shrink-0', active && 'text-sidebar-accent')}
        aria-hidden="true"
      />
      <span className="min-w-0">
        <span className="block font-medium">{t(labelKey)}</span>
        <span className="block truncate text-2xs text-sidebar-muted">{t(descriptionKey)}</span>
      </span>
    </Link>
  );
}

function OperatorNavLinks({ onNavigate }: { onNavigate?: (() => void) | undefined }): ReactNode {
  const isActive = useIsActive();
  const { t } = useTranslation();
  const { can } = useAuth();
  const [moreOpen, setMoreOpen] = useState(false);
  const items = OPERATOR_NAV.filter(
    (item) =>
      (item.adminOnly !== true || can('ADMIN')) && (item.superOnly !== true || can('SUPER')),
  );
  const primary = items.filter((item) => item.secondary !== true);
  const secondary = items.filter((item) => item.secondary === true);
  // The current page's link must never be hidden — force the group open while one is active.
  const open = moreOpen || secondary.some((item) => isActive(item.to));
  return (
    <nav aria-label={t('layout.shell.operatorSectionsAriaLabel')} className="space-y-1">
      {primary.map((item) => (
        <OperatorNavLink key={item.to} item={item} onNavigate={onNavigate} />
      ))}
      {secondary.length > 0 && (
        <>
          <button
            type="button"
            onClick={() => {
              setMoreOpen(!open);
            }}
            aria-expanded={open}
            className={cn(
              'flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-colors',
              'text-sidebar-muted hover:bg-white/5 hover:text-sidebar-foreground',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-accent',
            )}
          >
            <ChevronDown
              className={cn('mt-0.5 size-4 shrink-0 transition-transform', !open && '-rotate-90')}
              aria-hidden="true"
            />
            <span className="min-w-0 font-medium">{t('layout.shell.operatorNav.more.label')}</span>
          </button>
          {open &&
            secondary.map((item) => (
              <OperatorNavLink key={item.to} item={item} onNavigate={onNavigate} />
            ))}
        </>
      )}
    </nav>
  );
}

function OperatorIdentity(): ReactNode {
  const { session } = useAuth();
  if (!session) return null;
  return (
    <div className="flex items-center gap-3 rounded-lg bg-white/5 p-3">
      <span
        className="flex size-8 shrink-0 items-center justify-center rounded-full bg-sidebar-accent/20 text-xs font-semibold text-sidebar-accent"
        aria-hidden="true"
      >
        {initialsOf(session.display_name)}
      </span>
      <span className="min-w-0 flex-1 leading-tight">
        <span className="block truncate text-xs font-medium text-sidebar-foreground">
          {session.display_name}
        </span>
        {/* Null on a customer session whose website authenticates by something other than an
            email (`CustomerSession` in `@pf/bff-contract`). The line is dropped rather than
            rendered empty, so the block does not collapse to an odd half-height. */}
        {session.email === null ? null : (
          <span className="block truncate text-2xs text-sidebar-muted">{session.email}</span>
        )}
      </span>
      <Pill className="shrink-0 bg-sidebar-accent/15 text-sidebar-accent">{session.role}</Pill>
    </div>
  );
}

/** The operator console shell: persistent sidebar, dense content column. */
export function OperatorShell({ children }: { children: ReactNode }): ReactNode {
  const [menuOpen, setMenuOpen] = useState(false);
  const { t } = useTranslation();

  return (
    // The directory feeds every participant chip on every operator page — one mount, one query,
    // the same `/participants` the web-scope selector below keeps warm.
    <ParticipantDirectoryProvider>
      <div className="min-h-dvh bg-background">
        <a
          href="#main"
          className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-sm focus:text-primary-foreground"
        >
          {t('layout.shell.skipToContent')}
        </a>

        {/* Desktop sidebar */}
        <aside
          aria-label={t('layout.shell.consoleNavigationAriaLabel')}
          className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col gap-6 border-r border-sidebar-border bg-sidebar p-4 text-sidebar-foreground lg:flex"
        >
          <Link
            to="/ops/dashboard"
            className="rounded-lg px-1 py-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-accent"
          >
            <PaymentFlowLockup />
          </Link>
          <WebScopeSelect />
          <div className="flex-1 overflow-y-auto">
            <OperatorNavLinks />
          </div>
          <div className="space-y-3">
            <OperatorIdentity />
            <div className="flex items-center justify-between gap-2">
              <ThemeToggle className="border-sidebar-border" />
              <LanguageSwitcher className="border-sidebar-border" />
            </div>
            <SignOutButton className="w-full" />
          </div>
        </aside>

        {/* Mobile top bar */}
        <header className="sticky top-0 z-20 flex items-center justify-between gap-3 border-b border-border bg-sidebar px-4 py-3 text-sidebar-foreground lg:hidden">
          <PaymentFlowLockup />
          <WebScopeIndicator className="ml-auto" />
          <Dialog.Root open={menuOpen} onOpenChange={setMenuOpen}>
            <Dialog.Trigger asChild>
              <Button variant="ghost" size="icon" aria-label={t('layout.shell.openMenuAriaLabel')}>
                <Menu aria-hidden="true" />
              </Button>
            </Dialog.Trigger>
            <Dialog.Portal>
              <Dialog.Overlay className="fixed inset-0 z-40 bg-black/60" />
              <Dialog.Content className="fixed inset-y-0 right-0 z-50 flex w-72 flex-col gap-6 border-l border-sidebar-border bg-sidebar p-4 text-sidebar-foreground focus:outline-none">
                <div className="flex items-center justify-between">
                  <Dialog.Title className="text-sm font-semibold">
                    {t('layout.shell.navigationTitle')}
                  </Dialog.Title>
                  <Dialog.Close asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={t('layout.shell.closeMenuAriaLabel')}
                    >
                      <X aria-hidden="true" />
                    </Button>
                  </Dialog.Close>
                </div>
                <Dialog.Description className="sr-only">
                  {t('layout.shell.navigationDescription')}
                </Dialog.Description>
                <WebScopeSelect />
                <div className="flex-1 overflow-y-auto">
                  <OperatorNavLinks
                    onNavigate={() => {
                      setMenuOpen(false);
                    }}
                  />
                </div>
                <div className="space-y-3">
                  <OperatorIdentity />
                  <div className="flex items-center justify-between gap-2">
                    <ThemeToggle className="border-sidebar-border" />
                    <LanguageSwitcher className="border-sidebar-border" />
                  </div>
                  <SignOutButton className="w-full" />
                </div>
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>
        </header>

        <main id="main" className="lg:pl-64">
          <div className="mx-auto w-full max-w-[1400px] space-y-8 px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
            {children}
          </div>
        </main>
      </div>
    </ParticipantDirectoryProvider>
  );
}

// ---------------------------------------------------------------------------------------------
// Customer shell
// ---------------------------------------------------------------------------------------------

/** The participating website's cashier shell: light top bar, three tasks. */
export function CustomerShell({ children }: { children: ReactNode }): ReactNode {
  const isActive = useIsActive();
  const { session } = useAuth();
  const { t } = useTranslation();

  return (
    <div className="min-h-dvh bg-background">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-sm focus:text-primary-foreground"
      >
        {t('layout.shell.skipToContent')}
      </a>

      <header className="sticky top-0 z-20 border-b border-border bg-card/95 backdrop-blur">
        <div className="mx-auto flex w-full max-w-5xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <Link
            to="/app/deposit"
            className="rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <WebsiteLockup />
          </Link>
          <div className="flex items-center gap-2">
            {session ? (
              <span className="hidden items-center gap-2 text-xs text-muted-foreground sm:flex">
                <span
                  className="flex size-7 items-center justify-center rounded-full bg-accent text-2xs font-semibold text-accent-foreground"
                  aria-hidden="true"
                >
                  {initialsOf(session.display_name)}
                </span>
                <span className="max-w-40 truncate">{session.display_name}</span>
              </span>
            ) : null}
            {/* Embedded: the host website owns the colour scheme (it seeds `pf.theme` before
                handing off), so offering a toggle here would just fight it. */}
            {EMBEDDED_CASHIER ? null : <ThemeToggle />}
            <LanguageSwitcher />
            {EMBEDDED_CASHIER ? <BackToSiteButton /> : <SignOutButton variant="outline" />}
          </div>
        </div>

        <nav
          aria-label={t('layout.shell.cashierNavAriaLabel')}
          className="mx-auto w-full max-w-5xl px-4 sm:px-6"
        >
          <ul className="-mb-px flex gap-1 overflow-x-auto">
            {CUSTOMER_NAV.map(({ to, labelKey, icon: Icon }) => {
              const active = isActive(to);
              return (
                <li key={to}>
                  <Link
                    to={to}
                    aria-current={active ? 'page' : undefined}
                    className={cn(
                      'flex items-center gap-2 whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-medium transition-colors',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      active
                        ? 'border-primary text-foreground'
                        : 'border-transparent text-muted-foreground hover:text-foreground',
                    )}
                  >
                    <Icon className="size-4" aria-hidden="true" />
                    {t(labelKey)}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
      </header>

      <main id="main">
        <div className="mx-auto w-full max-w-5xl space-y-6 px-4 py-6 sm:px-6 sm:py-8">
          {children}
        </div>
      </main>

      <footer className="mx-auto w-full max-w-5xl px-4 pb-8 sm:px-6">
        <p className="rounded-lg border border-dashed border-border px-4 py-3 text-2xs leading-relaxed text-muted-foreground">
          {t('layout.shell.customerFooter')}
        </p>
      </footer>
    </div>
  );
}
